/**
 * Style Quiz — five visual questions that map onto Shopify Storefront queries.
 *
 * Everything here is a *query input*: the answers pick a product_type set, an
 * optional `title:<material>` term and an optional price bound. No product data
 * is invented, and no claim is made about the products a board surfaces beyond
 * "these match the filters you chose".
 */

export type QuestionId = "room" | "palette" | "material" | "feel" | "budget";

export interface QuizOption {
  value: string;
  label: string;
  /** Optional existing editorial image reused as a thumbnail. */
  image?: string;
  hint?: string;
}

export interface QuizQuestion {
  id: QuestionId;
  question: string;
  options: QuizOption[];
}

export const QUIZ_QUESTIONS: QuizQuestion[] = [
  {
    id: "room",
    question: "What are you furnishing?",
    options: [
      { value: "living", label: "Living room", image: "/journal/28-hero.webp" },
      { value: "bedroom", label: "Bedroom", image: "/journal/58-hero.webp" },
      { value: "dining", label: "Dining room", image: "/journal/31-mid.webp" },
      { value: "workspace", label: "Workspace", image: "/journal/64-hero.webp" },
    ],
  },
  {
    id: "palette",
    question: "Which palette feels like home?",
    options: [
      { value: "warm", label: "Warm neutrals", image: "/journal/29-mid.webp" },
      { value: "cool", label: "Cool greys", image: "/journal/74-hero.webp" },
      { value: "earth", label: "Earth & terracotta", image: "/journal/85-hero.webp" },
      { value: "contrast", label: "High contrast", image: "/journal/96-hero.webp" },
    ],
  },
  {
    id: "material",
    question: "Which materials do you reach for?",
    options: [
      { value: "oak", label: "Oak & linen", image: "/journal/22-hero.webp" },
      { value: "walnut", label: "Walnut & leather", image: "/journal/66-hero.webp" },
      { value: "boucle", label: "Bouclé & cream", image: "/journal/91-hero.webp" },
      { value: "rattan", label: "Rattan & natural", image: "/journal/99-hero.webp" },
    ],
  },
  {
    id: "feel",
    question: "How should the room feel?",
    options: [
      { value: "calm", label: "Calm & minimal", image: "/journal/20-mid.webp" },
      { value: "layered", label: "Warm & layered", image: "/journal/58-mid.webp" },
      { value: "sculptural", label: "Bold & sculptural", image: "/journal/87-mid.webp" },
    ],
  },
  {
    id: "budget",
    question: "Where's your budget per piece?",
    options: [
      { value: "250", label: "Under $250" },
      { value: "600", label: "$250–600" },
      { value: "1500", label: "$600–1,500" },
      { value: "none", label: "No limit" },
    ],
  },
];

export type QuizAnswers = Partial<Record<QuestionId, string>>;

export const ROOM_LABELS: Record<string, string> = {
  living: "Living Room",
  bedroom: "Bedroom",
  dining: "Dining Room",
  workspace: "Workspace",
};

const FEEL_NAMES: Record<string, string> = {
  calm: "Calm Minimal",
  layered: "Warm Layered",
  sculptural: "Bold Sculptural",
};

const PALETTE_LABELS: Record<string, string> = {
  warm: "warm neutrals",
  cool: "cool greys",
  earth: "earth and terracotta",
  contrast: "high contrast",
};

const MATERIAL_LABELS: Record<string, string> = {
  oak: "oak and linen",
  walnut: "walnut and leather",
  boucle: "bouclé and cream",
  rattan: "rattan and natural fibres",
};

/** Material search term used in `title:<term>` — kept to words that exist in feed titles. */
const MATERIAL_TERMS: Record<string, string> = {
  oak: "oak",
  walnut: "walnut",
  boucle: "boucle",
  rattan: "rattan",
};

/** Palette contributes a secondary colour term used when the material term fails. */
const PALETTE_TERMS: Record<string, string> = {
  warm: "beige",
  cool: "grey",
  earth: "terracotta",
  contrast: "black",
};

const PRICE_BOUNDS: Record<string, string> = {
  "250": "variants.price:<250",
  "600": "variants.price:>=250 AND variants.price:<=600",
  "1500": "variants.price:>=600 AND variants.price:<=1500",
};

export interface BoardSlot {
  role: string;
  productType: string;
}

export const ROOM_SLOTS: Record<string, BoardSlot[]> = {
  living: [
    { role: "Seating", productType: "Sofas" },
    { role: "Coffee table", productType: "Coffee Tables" },
    { role: "Lighting", productType: "Table Lamps" },
    { role: "Rug", productType: "Outdoor Rugs" },
    { role: "Accent chair", productType: "Accent Chairs" },
    { role: "Decor", productType: "Vases" },
  ],
  bedroom: [
    { role: "Bed", productType: "Standard Beds" },
    { role: "Nightstand", productType: "Nightstands & Bedside Tables" },
    { role: "Lighting", productType: "Table Lamps" },
    { role: "Textiles", productType: "Throws & Blankets" },
    { role: "Mirror", productType: "Mirrors" },
    { role: "Decor", productType: "Vases" },
  ],
  dining: [
    { role: "Dining table", productType: "Dining Tables" },
    { role: "Dining chairs", productType: "Dining Chairs" },
    { role: "Lighting", productType: "Chandeliers" },
    { role: "Rug", productType: "Outdoor Rugs" },
    { role: "Sideboard", productType: "Sideboards" },
    { role: "Decor", productType: "Vases" },
  ],
  workspace: [
    { role: "Desk", productType: "Desks" },
    { role: "Office chair", productType: "Office Chairs" },
    { role: "Lighting", productType: "Floor Lamps" },
    { role: "Bookcase", productType: "Bookcases" },
    { role: "Rug", productType: "Outdoor Rugs" },
    { role: "Decor", productType: "Vases" },
  ],
};

/** Human-readable style name, e.g. "Calm Minimal Living Room". */
export const styleName = (answers: QuizAnswers): string => {
  const feel = FEEL_NAMES[answers.feel || ""] || "Curated";
  const room = ROOM_LABELS[answers.room || ""] || "Room";
  return `${feel} ${room}`;
};

/** Upper price bound of the chosen budget, or null when no budget was set. */
export const priceCeiling = (answers: QuizAnswers): number | null => {
  const map: Record<string, number> = { "250": 250, "600": 600, "1500": 1500 };
  return map[answers.budget || ""] ?? null;
};

export const styleSubline = (answers: QuizAnswers): string => {
  const palette = PALETTE_LABELS[answers.palette || ""];
  const material = MATERIAL_LABELS[answers.material || ""];
  const parts: string[] = [];
  if (palette) parts.push(palette);
  if (material) parts.push(material);
  const base = parts.length ? `Built around ${parts.join(", ")}.` : "";
  const budgetOption = QUIZ_QUESTIONS[4].options.find((o) => o.value === answers.budget);
  // The budget is a hard ceiling (src/lib/styleBoard.ts): a role with nothing
  // in budget is left off the board rather than filled with a pricier piece.
  const ceiling = priceCeiling(answers);
  const budget = budgetOption && ceiling ? ` Every piece is $${ceiling.toLocaleString("en-US")} or less.` : "";
  return `${base}${budget}`.trim();
};


/**
 * Fallback chain for one slot, most specific first. Every level always carries
 * `available_for_sale:true` (fetchProducts adds it), so a slot can never surface
 * a dead end. The in-budget levels come first: material alone used to outrank
 * the budget, putting a $2,964 oak sofa on an "Under $250" board.
 */
export const slotQueryChain = (productType: string, answers: QuizAnswers): string[] => {
  const typeTerm = /\s|&/.test(productType) ? `product_type:"${productType}"` : `product_type:${productType}`;
  const material = MATERIAL_TERMS[answers.material || ""];
  const palette = PALETTE_TERMS[answers.palette || ""];
  const price = PRICE_BOUNDS[answers.budget || ""];

  const chain: string[] = [];
  if (material && price) chain.push(`${typeTerm} AND title:${material} AND ${price}`);
  if (palette && price) chain.push(`${typeTerm} AND title:${palette} AND ${price}`);
  if (price) chain.push(`${typeTerm} AND ${price}`);
  if (material) chain.push(`${typeTerm} AND title:${material}`);
  if (palette) chain.push(`${typeTerm} AND title:${palette}`);
  chain.push(typeTerm);
  return Array.from(new Set(chain));
};

export const answersToSearchParams = (answers: QuizAnswers): string => {
  const params = new URLSearchParams();
  (["room", "palette", "material", "feel", "budget"] as QuestionId[]).forEach((k) => {
    if (answers[k]) params.set(k, answers[k] as string);
  });
  return params.toString();
};

export const parseAnswers = (params: URLSearchParams): QuizAnswers => {
  const answers: QuizAnswers = {};
  QUIZ_QUESTIONS.forEach((q) => {
    const raw = params.get(q.id);
    if (raw && q.options.some((o) => o.value === raw)) answers[q.id] = raw;
  });
  return answers;
};

export const isCompleteAnswers = (answers: QuizAnswers): boolean =>
  QUIZ_QUESTIONS.every((q) => Boolean(answers[q.id]));
